'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';

interface ResourceDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  resource: {
    title: string;
    pages?: string;
    language: string;
    pdfFile: string;
    type: string;
  } | null;
}

export default function ResourceDownloadModal({
  isOpen,
  onClose,
  resource,
}: ResourceDownloadModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setEmail('');
      setCompany('');
      setError('');
      setSuccess(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen || !resource) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const res = await fetch('/api/submit-resource-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          company,
          resourceTitle: resource.title,
          resourceType: resource.type,
          pdfFile: resource.pdfFile,
          sourcePage: 'Resources',
        }),
      });

      const data = await res.json() as { success?: boolean; error?: string; signedUrl?: string };

      if (!res.ok || !data.success) {
        setError(data.error || 'Something went wrong. Please try again.');
        setIsSubmitting(false);
        return;
      }

      setSuccess(true);

      // Use signed URL from Supabase Storage — never fall back to local path
      const downloadUrl = data.signedUrl;
      if (downloadUrl) {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.click();
      } else {
        // Signed URL generation failed — inform the user
        setSuccess(false);
        setError('Download link could not be generated. Please contact support.');
        setIsSubmitting(false);
        return;
      }

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch {
      setError('Network error. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl p-8"
        style={{ backgroundColor: 'rgb(18, 22, 32)', border: '1px solid rgba(85,136,251,0.15)' }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-gray-400 hover:text-white transition-colors"
          aria-label="Close modal"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6 6 18" /><path d="m6 6 12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="flex items-start gap-4 mb-7">
          <div
            className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: 'rgba(43,98,239,0.2)', border: '1px solid rgba(43,98,239,0.3)' }}
          >
            {resource.type === 'Industry Report' ? (
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="rgb(85,136,251)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3v16a2 2 0 0 0 2 2h16" />
                <path d="M18 17V9" />
                <path d="M13 17V5" />
                <path d="M8 17v-3" />
              </svg>
            ) : (
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="rgb(85,136,251)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                <path d="M10 9H8" />
                <path d="M16 13H8" />
                <path d="M16 17H8" />
              </svg>
            )}
          </div>
          <div>
            <p className="text-xs font-mono tracking-wider uppercase mb-1" style={{ color: 'rgb(85,136,251)' }}>
              {resource.type}
            </p>
            <h2 className="font-bold text-xl leading-snug mb-1" style={{ color: 'oklch(95% .005 265)' }}>
              {resource.title}
            </h2>
            <p className="text-sm" style={{ color: 'oklch(65% .01 265)' }}>
              PDF{resource.pages ? ` · ${resource.pages}` : ''} · Language: {resource.language}
            </p>
          </div>
        </div>

        {success ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(43,98,239,0.2)' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgb(85,136,251)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" />
              </svg>
            </div>
            <p className="font-semibold text-lg mb-1" style={{ color: 'oklch(95% .005 265)' }}>Download Starting…</p>
            <p className="text-sm" style={{ color: 'oklch(65% .01 265)' }}>Your PDF should open in a new tab.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {/* Full Name */}
            <div className="mb-5">
              <label className="block text-sm font-semibold mb-2" style={{ color: 'oklch(95% .005 265)' }}>
                Full Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-4 py-3.5 rounded-xl text-sm outline-none transition-all"
                style={{
                  backgroundColor: 'rgb(8, 11, 18)',
                  border: '1px solid rgba(85,136,251,0.2)',
                  color: 'oklch(95% .005 265)',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(85,136,251,0.6)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(85,136,251,0.2)'; }}
              />
            </div>

            {/* Work Email */}
            <div className="mb-5">
              <label className="block text-sm font-semibold mb-2" style={{ color: 'oklch(95% .005 265)' }}>
                Work Email <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3.5 rounded-xl text-sm outline-none transition-all"
                style={{
                  backgroundColor: 'rgb(8, 11, 18)',
                  border: '1px solid rgba(85,136,251,0.2)',
                  color: 'oklch(95% .005 265)',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(85,136,251,0.6)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(85,136,251,0.2)'; }}
              />
            </div>

            {/* Company */}
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2" style={{ color: 'oklch(95% .005 265)' }}>
                Company <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="Company name"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                required
                className="w-full px-4 py-3.5 rounded-xl text-sm outline-none transition-all"
                style={{
                  backgroundColor: 'rgb(8, 11, 18)',
                  border: '1px solid rgba(85,136,251,0.2)',
                  color: 'oklch(95% .005 265)',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(85,136,251,0.6)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(85,136,251,0.2)'; }}
              />
            </div>

            {error && (
              <p className="text-sm mb-4 text-center" style={{ color: '#ef4444' }}>{error}</p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-3 py-4 rounded-xl font-bold text-base text-white transition-all hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: 'rgb(79, 107, 237)' }}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  Processing…
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download Resource
                </>
              )}
            </button>

            {/* Privacy */}
            <p className="text-center text-xs mt-4" style={{ color: 'oklch(65% .01 265)' }}>
              By submitting, you agree to our{' '}
              <Link href="/privacy-policy" className="hover:underline" style={{ color: 'rgb(85,136,251)' }}>
                Privacy Policy
              </Link>
              .
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
